import { useEffect, useState } from 'react';
import {
  Card,
  Divider,
  NativeSelect,
  Skeleton,
  Stack,
  Button,
  Box,
  Typography,
} from '@mui/material';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { debounce } from 'lodash';
import { getPatientInfo } from 'apis/admin';
import useNotification from 'hooks/useNotification';
import useStudent from 'store/student/useStudent';
import usePatient from 'store/patient/usePatient';
import PatientInfoItem from './PatientInfoItem';
import useSurvey from 'store/survey/useSurvey';
import SmartnurseIcon from 'assets/smartnurse-icon.svg';
import no_profile from 'assets/no_profile.svg';

interface Props {
  menuDrawerWidth: number;
  setMenuDrawerWidth: (menuDrawerWidth: number) => void;
}

const PatientInfo = (props: Props) => {
  const { menuDrawerWidth, setMenuDrawerWidth } = props;
  const { onFail } = useNotification();
  const { student_name: userName } = useStudent();
  const { onUpdateSurveyType } = useSurvey();

  const { patient, patientInfo, onSelectedPatientInfo } = usePatient();
  console.log('patientInfo:', patient);
  const [width, setWidth] = useState(window.innerWidth);

  const handleResize = debounce(() => {
    setWidth(window.innerWidth);
  }, 200);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (width < 1194) {
      setMenuDrawerWidth(52);
    } else {
      setMenuDrawerWidth(300);
    }
  }, [width]);

  // 바코드 모달창 만들기

  const onClickModal = (label: string) => {
    onUpdateSurveyType(label);
  };

  // 부진단 코드 index
  const [diseaseSubIndex, setDiseaseSubIndex] = useState(0);

  useEffect(() => {
    if (!patient) return;

    // 가상환자 상세정보 요청
    getPatientInfo({ patient_id: patient.patient_id })
      .then(({ data }) => {
        onSelectedPatientInfo(data);
      })
      .catch(e => {
        onSelectedPatientInfo(null);
        onFail(`가상환자 데이터 조회에 실패했습니다.`, e);
      });
    // eslint-disable-next-line
  }, [patient]);

  if (!patientInfo) {
    return <Skeleton variant="rectangular" sx={{ flex: 1 }} />;
  }
  const {
    // column1
    patient_id,
    name,
    age,
    gender,
    blood,
    // column2
    height,
    weight,
    admin_hod,
    admin_pod,
    // column3
    department,
    ward,
    room,
    main_doctor,
    // column4
    disease_main,
    disease_sub,
    fc,
  } = patientInfo;

  // 부진단 코드 선택 UI
  const DiseaseSubCode = () => (
    <NativeSelect
      value={diseaseSubIndex}
      onChange={e => setDiseaseSubIndex(Number(e.target.value))}
      inputProps={{ style: { paddingTop: 0, paddingBottom: 0 } }}
      sx={{ fontSize: 14, lineHeight: '18px' }}
    >
      {disease_sub?.map(({ disease_id }, index) => (
        <option key={disease_id} value={index}>
          {disease_id}
        </option>
      ))}
    </NativeSelect>
  );

  const convertHeight = (heightStr: string) => {
    // "160cm"에서 숫자만 추출
    const cmValue = parseFloat(heightStr);
    if (isNaN(cmValue)) return heightStr;
    // cm를 인치로 변환
    const totalInches = cmValue / 2.54;
    // 전체 인치에서 피트와 남은 인치 계산
    const feet = Math.floor(totalInches / 12);
    const inches = Math.round(totalInches - feet * 12);
    return `${cmValue}cm (${feet}'${inches}")`;
  };

  const convertWeight = (weightStr: string) => {
    // "52kg"에서 숫자만 추출
    const kgValue = parseFloat(weightStr);
    if (isNaN(kgValue)) return weightStr;
    // kg를 파운드로 변환
    const pounds = kgValue * 2.20462;
    return `${kgValue}kg (${pounds.toFixed(1)} lbs)`;
  };

  const toList = (v?: string | null) =>
    (v ?? '')
      .split(',')
      .map(s => s.trim())
      .filter(Boolean);

  const AllergySection = () => {
    const drug = toList(patientInfo?.allergy_drug);
    const food = toList(patientInfo?.allergy_food);
    const env = toList(patientInfo?.allergy_environmental);

    const Section = ({ title, items }: { title: string; items: string[] }) => (
      <>
        <Typography sx={{ mt: 1.5, mb: 0.5, fontWeight: 600, fontSize: 14 }}>
          {title}
        </Typography>
        <Box component="ul" sx={{ pl: 3, m: 0 }}>
          {items.length ? (
            items.map((it, idx) => (
              <Typography
                component="li"
                key={`${title}-${idx}`}
                sx={{ fontSize: 13, color: '#333' }}
              >
                {it}
              </Typography>
            ))
          ) : (
            <Typography
              component="li"
              sx={{ color: '#999', fontSize: 13, listStyle: 'none', ml: -3 }}
            >
              None
            </Typography>
          )}
        </Box>
      </>
    );

    return (
      <Box
        sx={{
          backgroundColor: '#D6E5F9',
          borderRadius: '8px',
          p: 2,
          mb: 1.5,
        }}
      >
        <Typography sx={{ fontWeight: 700, mb: 0.5, fontSize: 16 }}>
          Allergy
        </Typography>
        <Section title="Drug" items={drug} />
        <Section title="Food" items={food} />
        <Section title="Environmental" items={env} />
      </Box>
    );
  };

  const PrecautionsSection = () => {
    const items = toList(patientInfo?.caution);

    return (
      <Box
        sx={{
          backgroundColor: '#D6E5F9',
          borderRadius: '8px',
          p: 2,
        }}
      >
        <Typography sx={{ fontWeight: 700, mb: 1, fontSize: 16 }}>
          Precautions
        </Typography>

        <Box component="ul" sx={{ pl: 3, m: 0 }}>
          {items.length ? (
            items.map((it, idx) => (
              <Typography
                component="li"
                key={`precaution-${idx}`}
                sx={{ fontSize: 13, color: '#333' }}
              >
                {it}
              </Typography>
            ))
          ) : (
            <Typography
              component="li"
              sx={{ color: '#999', fontSize: 13, listStyle: 'none', ml: -3 }}
            >
              None
            </Typography>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <div style={{ zIndex: 1000 }}>
      {menuDrawerWidth !== 300 ? (
        <Box
          className="userDiv"
          style={{ lineHeight: '24px', paddingTop: '5px' }}
          sx={{ whiteSpace: 'pre-wrap' }}
        >
          <ArrowForwardIosIcon
            className="hamburgerIcon"
            fontSize="small"
            onClick={() => {
              if (menuDrawerWidth !== 300) setMenuDrawerWidth(300);
              else setMenuDrawerWidth(52);
            }}
            sx={{ marginTop: '0px', color: '#2B308B' }}
          />
          {menuDrawerWidth === 300}
        </Box>
      ) : (
        <>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
            }}
          >
            {menuDrawerWidth === 300 ? (
              <Box
                height={48}
                component="img"
                alt={'SmartNurse'}
                src={SmartnurseIcon}
                sx={{ objectFit: 'contain', marginBottom: '20px' }}
              />
            ) : null}
            <ArrowForwardIosIcon
              className="hamburgerIcon"
              fontSize="small"
              onClick={() => {
                if (menuDrawerWidth !== 300) setMenuDrawerWidth(300);
                else setMenuDrawerWidth(52);
              }}
              sx={{ marginTop: '20px', marginLeft: '240px', color: '#2B308B' }}
            />
            {menuDrawerWidth === 300}
          </Box>

          <Card
            component="section"
            sx={{
              p: 2,
              overflow: 'visible',
              justifyContent: 'space-between',
              gap: { xs: 1.25, xl: 2.5 },
              backgroundColor: '#B1CCF5',
            }}
          >
            <Stack spacing={1.25} sx={{ marginBottom: '10px' }}>
              <Box
                height={183}
                component="img"
                alt={'SmartNurse'}
                src={patient?.photo ? patient?.photo : no_profile}
                sx={{
                  objectFit: 'contain',
                  marginBottom: '20px',
                  borderRadius: '8px',
                }}
              />
            </Stack>
            <Stack spacing={1.25} sx={{ marginBottom: '10px' }}>
              <PatientInfoItem title="Reg. No." content={patient_id} />
              <PatientInfoItem title="Name" content={name} />
              <PatientInfoItem title="Age" content={age} />
            </Stack>
            <Divider orientation="vertical" />
            <Stack spacing={1.25} sx={{ margin: '10px 0 10px 0' }}>
              <PatientInfoItem title="Blood Type" content={blood} />
              <PatientInfoItem title="Height" content={convertHeight(height)} />
              <PatientInfoItem title="Weight" content={convertWeight(weight)} />
              <PatientInfoItem title="HOD" content={admin_hod} />
              <PatientInfoItem title="POD" content={admin_pod} />
            </Stack>
            <Divider orientation="vertical" />
            <Stack spacing={1.25} sx={{ margin: '10px 0 10px 0' }}>
              <PatientInfoItem title="Department" content={department} />
              <PatientInfoItem title="Doctor" content={main_doctor} />
              <PatientInfoItem title="Ward" content={ward} />
              <PatientInfoItem title="Room" content={room} />
              <PatientInfoItem title="Nurse" content={userName} />
            </Stack>
            <Divider orientation="vertical" />
            <Stack spacing={1.25} sx={{ margin: '10px 0 10px 0' }}>
              <PatientInfoItem title="Code" content={disease_main.disease_id} />
              <PatientInfoItem
                title="Diagnosis"
                content={disease_main.disease_eng}
              />
              <PatientInfoItem
                title="Code"
                content={
                  disease_sub.length > 0 ? (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'right',
                        width: '140px',
                      }}
                    >
                      <DiseaseSubCode />
                    </Box>
                  ) : null
                }
              />
              <PatientInfoItem
                title="Subdiagnosis"
                content={disease_sub[diseaseSubIndex]?.disease_eng}
              />
              <PatientInfoItem
                title="FC"
                content={
                  fc ? (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'right',
                        width: '140px',
                        fontSize: '14px',
                        color: 'red',
                      }}
                    >
                      {fc}
                    </Box>
                  ) : (
                    '-'
                  )
                }
              />
            </Stack>
            <Divider orientation="vertical" />
          </Card>

          <Box sx={{ mt: 2 }}>
            <AllergySection />
            <PrecautionsSection />
          </Box>
          <Box display="flex" justifyContent="center">
            <Button
              variant="contained"
              size="small"
              sx={{
                margin: '20px auto 5px auto',
                backgroundColor: '#2D73DC',
                color: 'white',
              }}
              onClick={() => onClickModal('Patient Label Printing')}
            >
              Patient Label Printing
            </Button>
          </Box>
        </>
      )}
    </div>
  );
};

export default PatientInfo;
